import utils from '../../utils'

const DEFAULT_MENU_SIZE = 54
const DEFAULT_MENU_RADIUS = 74
const DEFAULT_HIDE_DELAY = 60
const DEFAULT_START_ANGLE = -90
const DEFAULT_ITEM_SIZE_RATIO = 0.5
const DEFAULT_RADIUS_ITEM_RATIO = 0.85
const DEFAULT_RADIUS_PADDING = 8
const DEFAULT_MIN_ITEM_SIZE = 30
const DEFAULT_MAX_ITEM_SIZE = 72
const DEFAULT_ICON_SIZE_RATIO = 0.5
const DEFAULT_SAFE_AREA_PADDING = 12
const FULL_CIRCLE_DEGREES = 360

const defaults = {
	enabled: true,
	selector: 'node',
	actions: [],
	itemSize: DEFAULT_MENU_SIZE,
	radius: DEFAULT_MENU_RADIUS,
	itemSizeRatio: DEFAULT_ITEM_SIZE_RATIO,
	radiusItemRatio: DEFAULT_RADIUS_ITEM_RATIO,
	radiusPadding: DEFAULT_RADIUS_PADDING,
	minItemSize: DEFAULT_MIN_ITEM_SIZE,
	maxItemSize: DEFAULT_MAX_ITEM_SIZE,
	iconSizeRatio: DEFAULT_ICON_SIZE_RATIO,
	safeAreaPadding: DEFAULT_SAFE_AREA_PADDING,
	startAngle: DEFAULT_START_ANGLE,
	radiusBasis: 'max',
	hideDelay: DEFAULT_HIDE_DELAY,
	hideOnHoverOut: true,
	nodeFilter: () => true,
	beforeShow: () => true,
	beforeClose: () => true,
	action: () => {},
}

class NodeContextMenu {
	constructor(cy, params) {
		this.cy = cy
		this._options = utils.extend(true, {}, defaults, params)
		this._listeners = {}
		this._activeNode = null
		this._isMenuHovered = false
		this._hideTimeout = null
		this._isPointerInsideSafeArea = false
		this._menuMetrics = null
		this._suppressedNodeTap = null
		this._init()
	}

	_init() {
		this._initContainer()
		this._initEvents()
	}

	_initContainer() {
		this._container = this.cy.container()
		this.menu = document.createElement('div')
		this.menu.className = 'cy-editor-node-context-menu'
		this._container.append(this.menu)
	}

	_initEvents() {
		this._listeners.nodeMouseout = event => this._handleNodeMouseout(event)
		this._listeners.nodeTap = event => this._handleNodeTap(event)
		this._listeners.menuClick = event => this._handleMenuClick(event)
		this._listeners.menuTouchstart = event => this._handleMenuTouchstart(event)
		this._listeners.viewportChange = () => this._syncToViewport()
		this._listeners.tapstart = event => this._handleTapstart(event)
		this._listeners.pointerMove = event => this._handlePointerMove(event)
		this._listeners.close = event => this.close(event)
		this._listeners.nodeRemove = event => this._handleNodeRemove(event)

		this.menu.addEventListener('mousedown', this._listeners.menuClick)
		this.menu.addEventListener('touchstart', this._listeners.menuTouchstart, {passive: false})
		this._container.addEventListener('mousemove', this._listeners.pointerMove)
		this.cy.on('mouseout', this._options.selector, this._listeners.nodeMouseout)
		this.cy.on('tap', this._options.selector, this._listeners.nodeTap)
		this.cy.on('pan zoom resize', this._listeners.viewportChange)
		this.cy.on('tapstart', this._listeners.tapstart)
		this.cy.on('drag', this._listeners.close)
		this.cy.on('remove', this._options.selector, this._listeners.nodeRemove)
	}

	destroy() {
		clearTimeout(this._hideTimeout)
		this.menu.removeEventListener('mousedown', this._listeners.menuClick)
		this.menu.removeEventListener('touchstart', this._listeners.menuTouchstart)
		this._container.removeEventListener('mousemove', this._listeners.pointerMove)
		this.cy.off('mouseout', this._options.selector, this._listeners.nodeMouseout)
		this.cy.off('tap', this._options.selector, this._listeners.nodeTap)
		this.cy.off('pan zoom resize', this._listeners.viewportChange)
		this.cy.off('tapstart', this._listeners.tapstart)
		this.cy.off('drag', this._listeners.close)
		this.cy.off('remove', this._options.selector, this._listeners.nodeRemove)
		this.menu.remove()
	}

	enable() {
		this._options.enabled = true
	}

	disable() {
		this._options.enabled = false
		this.close()
	}

	suppressNextNodeTap(node) {
		this._suppressedNodeTap = node || true
	}

	setActions(actions) {
		this._options.actions = Array.isArray(actions) ? actions : []
		if(this.isShow){
			this._render()
			this._syncPosition()
		}
	}

	showForNode(node, event) {
		if(!this._canShowFor(node, event)){
			return
		}
		this._clearHideTimeout()
		this._isPointerInsideSafeArea = true
		this._activeNode = node
		this.show(event)
	}

	_handleNodeTap(event) {
		let node = event.target
		if(this._shouldSuppressNodeTap(node)){
			this._suppressedNodeTap = null
			return
		}
		if(!this._canShowFor(node, event)){
			return
		}
		this._clearHideTimeout()
		this._isPointerInsideSafeArea = true
		this._activeNode = node
		this.show(event)
	}

	_handleNodeMouseout(event) {
		if(!this._options.hideOnHoverOut){
			return
		}
		if(this._isEventInsideSafeArea(event?.originalEvent)){
			this._isPointerInsideSafeArea = true
			return
		}
		this._isPointerInsideSafeArea = false
		this._scheduleClose(event)
	}

	_handleTapstart(event) {
		if(this._isMenuDomEventTarget(event?.originalEvent?.target)){
			this._clearHideTimeout()
			return
		}
		if(this._isNodeEventTarget(event?.target)){
			this._clearHideTimeout()
			return
		}
		this.close(event)
	}

	_handlePointerMove(event) {
		if(!this.isShow || !this._activeNode || !this._options.hideOnHoverOut){
			return
		}
		if(this._isEventInsideSafeArea(event)){
			this._isPointerInsideSafeArea = true
			this._clearHideTimeout()
			return
		}
		this._isPointerInsideSafeArea = false
		this._scheduleClose(event)
	}

	_handleNodeRemove(event) {
		let removedNode = event?.target
		if(!this._activeNode || !removedNode?.same?.(this._activeNode)){
			return
		}
		this.close(event)
	}

	_handleMenuMouseenter() {
		this._isMenuHovered = true
		this._clearHideTimeout()
	}

	_handleMenuMouseleave() {
		this._isMenuHovered = false
		if(!this._options.hideOnHoverOut){
			return
		}
		if(!this._isPointerInsideSafeArea){
			this._scheduleClose()
		}
	}

	_handleMenuClick(event) {
		let item = event.target.closest('[data-node-context-menu-action]')
		if(!item){
			return
		}
		event.preventDefault()
		event.stopPropagation()

		let actionId = item.getAttribute('data-node-context-menu-action')
		let action = this._getVisibleActions().find(menuAction => menuAction.id === actionId)
		if(!action || action.disabled){
			return
		}

		let payload = {
			action,
			node: this._activeNode,
			originalEvent: event,
		}
		this.cy.trigger('cyeditor.nodeContextMenuAction', payload)
		this._options.action(payload)
		if(typeof action.onClick === 'function'){
			action.onClick(payload)
		}
	}

	_handleMenuTouchstart(event) {
		this._handleMenuClick(event)
	}

	_isNodeEventTarget(target) {
		return typeof target?.isNode === "function" && target.isNode()
	}

	_isMenuDomEventTarget(target) {
		return !!target?.closest?.('.cy-editor-node-context-menu')
	}

	_shouldSuppressNodeTap(node) {
		let suppressedNodeTap = this._suppressedNodeTap
		if(!suppressedNodeTap){
			return false
		}
		if(suppressedNodeTap === true){
			return true
		}
		return Boolean(node?.same?.(suppressedNodeTap))
	}

	_canShowFor(node, event) {
		let hasActions = this._getVisibleActions().length > 0
		let isEnabled = this._options.enabled
		let isAllowedNode = this._options.nodeFilter(node, event)
		return isEnabled && hasActions && isAllowedNode
	}

	show(event) {
		if(typeof this._options.beforeShow === 'function'){
			let shouldShow = this._options.beforeShow(event, this._activeNode)
			if(!shouldShow){
				return
			}
		}
		this._clearHideTimeout()
		utils.css(this.menu, {display: 'none'})
		this._syncPosition(true)
		this._render()
		this._syncPosition(true)
		utils.css(this.menu, {display: 'block'})
		this.isShow = true
	}

	close(event) {
		if(!this.isShow){
			return
		}
		if(typeof this._options.beforeClose === 'function'){
			let shouldClose = this._options.beforeClose(event, this._activeNode)
			if(!shouldClose){
				return
			}
		}
		utils.css(this.menu, {display: 'none'})
		this.menu.innerHTML = ''
		this.isShow = false
		this._activeNode = null
		this._menuMetrics = null
		this._isPointerInsideSafeArea = false
	}

	_scheduleClose(event) {
		if(this._hideTimeout){
			return
		}
		this._hideTimeout = setTimeout(() => {
			this._hideTimeout = null
			if(this._isMenuHovered){
				return
			}
			if(this._isPointerInsideSafeArea){
				return
			}
			this.close(event)
		}, this._options.hideDelay)
	}

	_clearHideTimeout() {
		clearTimeout(this._hideTimeout)
		this._hideTimeout = null
	}

	_render() {
		let actions = this._getVisibleActions()
		let metrics = this._getMenuMetrics()
		this._menuMetrics = metrics
		this.menu.innerHTML = ''
		actions.forEach((action, index) => {
			let button = this._createActionButton(action, index, actions.length, metrics)
			this.menu.append(button)
		})
	}

	_createActionButton(action, index, totalActions, metrics) {
		let button = document.createElement('button')
		let itemSize = metrics.itemSize
		let position = this._getActionPosition(index, totalActions, metrics)
		button.type = 'button'
		button.className = `node-context-menu-action ${action.disabled ? 'node-context-menu-action-disabled' : ''}`
		button.setAttribute('data-node-context-menu-action', action.id)
		button.setAttribute('aria-label', action.tooltip || action.title || action.id)
		button.addEventListener('mouseenter', () => this._handleMenuMouseenter())
		button.addEventListener('mouseleave', () => this._handleMenuMouseleave())
		utils.css(button, {
			width: `${itemSize}px`,
			height: `${itemSize}px`,
			left: `${position.x}px`,
			top: `${position.y}px`,
			background: action.color || action.background || '#276EF1',
			'--node-context-action-icon-size': `${itemSize * this._options.iconSizeRatio}px`,
		})
		this._appendIcon(button, action)
		this._appendTooltip(button, action)
		return button
	}

	_appendIcon(button, action) {
		let icon = document.createElement('i')
		let iconClass = action.iconClass || action.icon
		if(iconClass){
			icon.className = action.iconClass || `fontello ${iconClass}`
		}
		if(action.iconText){
			icon.textContent = action.iconText
			icon.className = 'node-context-menu-action-text-icon'
		}
		button.append(icon)
	}

	_appendTooltip(button, action) {
		let tooltipText = action.tooltip || action.title
		if(!tooltipText){
			return
		}
		let tooltip = document.createElement('span')
		tooltip.className = 'node-context-menu-action-tooltip'
		tooltip.textContent = tooltipText
		button.append(tooltip)
	}

	_getActionPosition(index, totalActions, metrics) {
		let angleStep = totalActions > 0 ? FULL_CIRCLE_DEGREES / totalActions : 0
		let angle = (this._options.startAngle + angleStep * index) * Math.PI / 180
		let radius = metrics.radius
		let itemOffset = metrics.itemSize / 2
		return {
			x: Math.cos(angle) * radius - itemOffset,
			y: Math.sin(angle) * radius - itemOffset,
		}
	}

	_getMenuMetrics() {
		let nodeSize = this._getRenderedNodeSize()
		let itemSize = this._clamp(
			nodeSize * this._options.itemSizeRatio,
			this._options.minItemSize,
			this._options.maxItemSize
		)
		let radius = nodeSize / 2 + itemSize * this._options.radiusItemRatio + this._options.radiusPadding
		let center = this._getActiveNodeRenderedPosition()
		let safeRadius = radius + itemSize / 2 + this._options.safeAreaPadding
		return {itemSize, radius, safeRadius, center}
	}

	_getRenderedNodeSize() {
		if(!this._activeNode){
			return this._options.itemSize
		}
		let zoom = this.cy.zoom()
		let renderedWidth = this._resolveRenderedDimension(this._activeNode, "renderedOuterWidth", "outerWidth", zoom)
		let renderedHeight = this._resolveRenderedDimension(this._activeNode, "renderedOuterHeight", "outerHeight", zoom)
		return this._options.radiusBasis === 'height' ? renderedHeight : Math.max(renderedWidth, renderedHeight)
	}

	_resolveRenderedDimension(node, renderedMethodName, modelMethodName, zoom) {
		if(typeof node[renderedMethodName] === "function"){
			return node[renderedMethodName]()
		}
		if(typeof node[modelMethodName] === "function"){
			return node[modelMethodName]() * zoom
		}
		return this._options.itemSize
	}

	_clamp(value, min, max) {
		return Math.min(max, Math.max(min, value))
	}

	_syncPosition(force = false) {
		if((!force && !this.isShow) || !this._activeNode){
			return
		}
		let position = this._getActiveNodeRenderedPosition()
		utils.css(this.menu, {
			left: `${position.x}px`,
			top: `${position.y}px`,
		})
	}

	_syncToViewport() {
		if(!this.isShow || !this._activeNode){
			return
		}
		this._syncPosition()
		this._render()
		this._syncPosition()
	}

	_getActiveNodeRenderedPosition() {
		if(!this._activeNode){
			return {x: 0, y: 0}
		}
		return this._activeNode.renderedPosition()
	}

	_isEventInsideSafeArea(event) {
		if(!event || !this._menuMetrics){
			return false
		}
		let point = this._getContainerPoint(event)
		return this._isPointInsideSafeArea(point)
	}

	_getContainerPoint(event) {
		let rect = this._container.getBoundingClientRect()
		return {
			x: event.clientX - rect.left,
			y: event.clientY - rect.top,
		}
	}

	_isPointInsideSafeArea(point) {
		let center = this._menuMetrics?.center
		let safeRadius = this._menuMetrics?.safeRadius
		if(!center || typeof safeRadius !== "number"){
			return false
		}
		let dx = point.x - center.x
		let dy = point.y - center.y
		return dx * dx + dy * dy <= safeRadius * safeRadius
	}

	_getVisibleActions() {
		return (this._options.actions || []).filter(action => action && action.id && action.visible !== false)
	}
}

export default cytoscape => {
	if(!cytoscape){
		return
	}

	cytoscape('core', 'nodeContextMenu', function(options) {
		return new NodeContextMenu(this, options)
	})
}
